import { useCallback, useState } from "react";
import { Box, Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faTrash,
  faExternalLinkAlt,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { authFetch } from "../utils/api";
import { guardWrite } from "../utils/permissions";
import { TelegramBrandIcon } from "./BrandIcons";
import ConfirmationModal from "./confirmationModal";
import "./TelegramLinker.css";

/**
 * TelegramLinker — Notification Center sub-component for the Telegram channel.
 *
 * Behaviour:
 *   * Renders a checkbox list of every Telegram chat the user has connected
 *     (one row per `telegram_links` entry returned by the backend).
 *   * "Connect new chat" requests a one-time deep-link from
 *     POST /api/notifications/telegram/start_link, opens it in a new tab
 *     and starts polling the links endpoint until the new chat appears
 *     (max ~2 min). The freshly added chat is auto-selected for this section.
 *   * Trash icon disconnects a link (DELETE) and refreshes the list.
 *
 * Props are intentionally narrow so the parent owns selection state
 * (per-section). This component never reads `prefs` itself.
 */
function TelegramLinker({
  sectionKey,
  links,
  selectedChatIds,
  onToggleChat,
  onLinksChanged,
}) {
  const { t } = useTranslation();
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  // Pending delete — set when the user clicks the trash icon, cleared
  // when the ConfirmationModal closes or the delete completes.
  const [pendingDelete, setPendingDelete] = useState(null);

  const startLink = useCallback(async () => {
    // Read-only org members must not be able to link new Telegram chats
    // — trigger the unified permission-denied modal and bail out.
    if (!guardWrite()) return;
    setLinking(true);
    setError(null);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}notifications/telegram/start_link`,
        { method: "POST", body: JSON.stringify({}) }
      );
      if (!res || !res.ok) {
        const detail = await safeReadDetail(res);
        throw new Error(detail || `HTTP ${res?.status}`);
      }
      const data = await res.json();
      if (!data?.deep_link) throw new Error("Missing deep_link in response");

      // Snapshot which chat_ids exist BEFORE we open Telegram so we can
      // detect the new one when polling.
      const known = new Set((links || []).map((l) => String(l.chat_id)));
      window.open(data.deep_link, "_blank", "noopener,noreferrer");
      pollForNewLink(known);
    } catch (e) {
      console.error("Telegram link start failed:", e);
      setError(t("notify_telegram_link_failed") || "Could not generate a link. Please try again.");
      setLinking(false);
    }
  }, [links, t]);

  const pollForNewLink = useCallback(
    async (knownChatIds) => {
      // Poll every 3s for up to 2 min. Stop early when we see a chat_id
      // that wasn't in the snapshot.
      const startedAt = Date.now();
      const TIMEOUT_MS = 120_000;
      const INTERVAL_MS = 3_000;
      while (Date.now() - startedAt < TIMEOUT_MS) {
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
        try {
          const res = await authFetch(
            `${process.env.REACT_APP_API_BASE_URL}notifications/telegram/links`
          );
          if (!res || !res.ok) continue;
          const data = await res.json();
          const next = Array.isArray(data?.links) ? data.links : [];
          const newOne = next.find((l) => !knownChatIds.has(String(l.chat_id)));
          if (newOne) {
            // Refresh parent state and auto-select the new chat for this
            // section to save the user a second click.
            onLinksChanged?.();
            onToggleChat(sectionKey, String(newOne.chat_id));
            setLinking(false);
            return;
          }
        } catch (_e) {
          /* keep polling */
        }
      }
      setLinking(false);
      setError(
        t("notify_telegram_link_timed_out") ||
          "We did not detect a new Telegram chat. Please try again."
      );
    },
    [onLinksChanged, onToggleChat, sectionKey, t]
  );

  const removeLink = useCallback(
    async (linkId) => {
      setRemovingId(linkId);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}notifications/telegram/links/${linkId}`,
          { method: "DELETE" }
        );
        if (!res || !res.ok) throw new Error(`HTTP ${res?.status}`);
        onLinksChanged?.();
      } catch (e) {
        console.error("Telegram link delete failed:", e);
        setError(
          t("notify_telegram_disconnect_failed") ||
            "Could not disconnect this chat. Please try again."
        );
      } finally {
        setRemovingId(null);
      }
    },
    [onLinksChanged, t]
  );

  const refresh = useCallback(async () => {
    setError(null);
    onLinksChanged?.();
  }, [onLinksChanged]);

  return (
    <div
      className="notification-emails-section telegram-linker"
      style={{
        "--brand-color": "#229ED9",
        "--brand-bg": "rgba(34, 158, 217, 0.10)",
      }}
    >
      <div className="notification-emails-section-heading">
        <span className="notification-emails-section-heading-icon">
          <TelegramBrandIcon size="18px" />
        </span>
        {t("notify_section_telegram_recipients") || "Telegram recipients"}
      </div>

      {(!links || links.length === 0) && (
        <p className="telegram-linker-empty-hint">
          {t("notify_telegram_empty_hint") ||
            "No Telegram chats connected yet. Click below to connect one."}
        </p>
      )}

      {links && links.length > 0 && (
        <div className="telegram-linker-list">
          {links.map((link) => {
            const id = String(link.chat_id);
            const checked = selectedChatIds.includes(id);
            return (
              <Box key={link.id} className="telegram-linker-row">
                <Checkbox
                  checked={checked}
                  onChange={() => onToggleChat(sectionKey, id)}
                  sx={{
                    "&.Mui-checked": { color: "#5616ea" },
                    "&:hover": { backgroundColor: "#5616ea15" },
                    padding: "4px",
                  }}
                />
                <span className="telegram-linker-row-label">
                  {link.display_name || `Chat ${id}`}
                  {link.chat_type ? (
                    <span className="telegram-linker-row-meta"> · {link.chat_type}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="telegram-linker-row-remove"
                  onClick={() => {
                    if (!guardWrite()) return;
                    setPendingDelete({
                      id: link.id,
                      label: link.display_name || `Chat ${id}`,
                    });
                  }}
                  disabled={removingId === link.id}
                  title={t("notify_telegram_disconnect") || "Disconnect"}
                >
                  {removingId === link.id ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faTrash} />
                  )}
                </button>
              </Box>
            );
          })}
        </div>
      )}

      <div className="telegram-linker-actions">
        <button
          type="button"
          className="telegram-linker-connect-btn"
          onClick={startLink}
          disabled={linking}
        >
          {linking ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>{t("notify_telegram_connect_pending") || "Waiting for Telegram…"}</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              <span>{t("notify_telegram_connect_button") || "Connect Telegram chat"}</span>
            </>
          )}
        </button>
        <button
          type="button"
          className="telegram-linker-refresh-btn"
          onClick={refresh}
          title={t("refresh") || "Refresh"}
        >
          <FontAwesomeIcon icon={faRotateRight} />
        </button>
      </div>
      {linking && (
        <p className="telegram-linker-help">
          {t("notify_telegram_connect_help") ||
            "Telegram will open in a new tab. Press START in the chat to finish linking."}
        </p>
      )}
      {error && <p className="telegram-linker-error">{error}</p>}
      <ConfirmationModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) removeLink(pendingDelete.id);
        }}
        itemName={pendingDelete?.label || ""}
      />
    </div>
  );
}

async function safeReadDetail(res) {
  if (!res) return null;
  try {
    const data = await res.json();
    return typeof data?.detail === "string" ? data.detail : null;
  } catch (_e) {
    return null;
  }
}

export default TelegramLinker;
